import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import {
  Page,
  NamedLink,
  ProviderAvatarMedium,
  IconSpinner,
  ReviewRating,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { queryProvidersAction } from './FarmSearchPage.duck';

import css from './FarmSearchPage.module.css';

/**
 * FarmSearchPageComponent
 *
 * Displays a grid of farm provider cards
 * Shows their avatar, displayName (farm name), bio, and profile link
 *
 * @component
 */

const ProviderCard = ({ provider }) => {
  const providerId = provider.id.uuid;
  const displayName = provider.attributes.profile.displayName;
  const bio = provider.attributes.profile.bio;
  const metadata = provider.attributes.profile.metadata || {};

  // reviewCount and reviewRating are maintained by the event handler
  const reviewCount = metadata.reviewCount ?? 0;
  const reviewRating = metadata.reviewRating ?? null;
  const hasReviews = reviewCount > 0 && reviewRating != null;

  return (
    <div className={css.providerCard}>
      <div className={css.cardMedia}>
        <ProviderAvatarMedium className={css.cardMediaImage} user={provider} disableProfileLink />
      </div>
      <div className={css.cardBody}>
        <div className={css.cardHeader}>
          <h3 className={css.providerName}>{displayName}</h3>
          <p className={css.providerBio}>
            {bio || <FormattedMessage id="FarmSearchPage.noBioPlaceholder" />}
          </p>
          <div className={css.reviewsRow}>
            {hasReviews ? (
              <>
                <ReviewRating
                  rating={Math.round(reviewRating)}
                  reviewStarClassName={css.reviewStar}
                />
                <span className={css.reviewCount}>
                  <FormattedMessage
                    id="FarmSearchPage.reviewCount"
                    values={{ count: reviewCount }}
                  />
                </span>
              </>
            ) : (
              <span className={css.noReviews}>
                <FormattedMessage id="FarmSearchPage.noReviews" />
              </span>
            )}
          </div>
        </div>
        <div className={css.cardFooter}>
          <NamedLink
            className={css.viewProfileButton}
            name="ProfilePage"
            params={{ id: providerId }}
          >
            <FormattedMessage id="FarmSearchPage.viewProfile" />
          </NamedLink>
        </div>
      </div>
    </div>
  );
};

export const FarmSearchPageComponent = props => {
  const {
    scrollingDisabled,
    providers,
    queryProvidersInProgress,
    queryProvidersError,
    onQueryProviders,
  } = props;
  const intl = useIntl();

  const [sortOrder, setSortOrder] = useState('asc');

  const pageTitle = intl.formatMessage({ id: 'FarmSearchPage.title' });

  const hasProviders = providers && providers.length > 0;

  const handleSortChange = e => {
    const newOrder = e.target.value;
    setSortOrder(newOrder);
    onQueryProviders({ page: 1, sortOrder: newOrder });
  };

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={pageTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'CollectionPage',
        name: pageTitle,
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.headingBlock}>
            <h1 className={css.title}>
              <FormattedMessage id="FarmSearchPage.title" />
            </h1>
            <p className={css.titleDescription}>
              <FormattedMessage id="FarmSearchPage.description" />
            </p>
          </div>

          <div className={css.sortBar}>
            <label className={css.sortLabel} htmlFor="farm-sort">
              <FormattedMessage id="FarmSearchPage.sortLabel" />
            </label>
            <div className={css.sortSelectWrapper}>
              <select
                id="farm-sort"
                className={css.sortSelect}
                value={sortOrder}
                onChange={handleSortChange}
                disabled={queryProvidersInProgress}
              >
                <option value="asc">
                  {intl.formatMessage({ id: 'FarmSearchPage.sortAZ' })}
                </option>
                <option value="desc">
                  {intl.formatMessage({ id: 'FarmSearchPage.sortZA' })}
                </option>
              </select>
            </div>
          </div>

          {queryProvidersInProgress ? (
            <div className={css.loading}>
              <IconSpinner />
              <FormattedMessage id="FarmSearchPage.loading" />
            </div>
          ) : queryProvidersError ? (
            <div className={css.error}>
              <FormattedMessage id="FarmSearchPage.error" />
            </div>
          ) : !hasProviders ? (
            <div className={css.noResults}>
              <FormattedMessage id="FarmSearchPage.noResults" />
            </div>
          ) : (
            <div className={css.providersList}>
              {providers.map(provider => (
                <ProviderCard key={provider.id.uuid} provider={provider} />
              ))}
            </div>
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { providers, queryProvidersInProgress, queryProvidersError } = state.FarmSearchPage;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    providers,
    queryProvidersInProgress,
    queryProvidersError,
  };
};

const mapDispatchToProps = dispatch => ({
  onQueryProviders: ({ page, sortOrder }) => dispatch(queryProvidersAction({ page, sortOrder })),
});

const FarmSearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(FarmSearchPageComponent);

export default FarmSearchPage;

// Simple layout wrapper
const LayoutSingleColumn = props => {
  const { topbar, footer, children } = props;
  return (
    <div className={css.layout}>
      {topbar}
      <main className={css.main}>{children}</main>
      {footer}
    </div>
  );
};
